import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  HStack,
  Portal,
  Text,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

const SESSION_KEY = "practice_tour_seen";
const POPOVER_WIDTH = 360;
const POPOVER_MAX_HEIGHT = 220;
const VIEWPORT_MARGIN = 12;
const ARROW_SIZE = 8;
const HIGHLIGHT_PADDING = 6;

/**
 * Lightweight onboarding tour for the practice/training screen.
 *
 * Walks the candidate through 4 key elements (timer, question text, verify,
 * microphone). Each step:
 *   - Computes the target's bounding rect.
 *   - Places the popover BELOW the target if there's enough viewport room,
 *     otherwise ABOVE. Horizontally centered on the target's center, clamped
 *     to the viewport so it never spills off-screen.
 *   - Draws a soft purple highlight ring around the target.
 *   - Repositions on scroll / resize.
 *
 * Shown once per browser session — set `practice_tour_seen` in sessionStorage
 * to skip on reload.
 */
const PracticeTour = ({ targets }) => {
  const { t } = useTranslation();
  const [stepIndex, setStepIndex] = useState(-1);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const id = setTimeout(() => setStepIndex(0), 300);
    return () => clearTimeout(id);
  }, []);

  const steps = [
    {
      key: "timer",
      ref: targets.timer,
      title: t("evaluation.practice.tour.timerTitle", "Timer"),
      body: t(
        "evaluation.practice.tour.timer",
        "Each question has a countdown. The bar turns red when time is running low. The practice question doesn't auto-submit — take as long as you need."
      ),
    },
    {
      key: "questionText",
      ref: targets.questionText,
      title: t(
        "evaluation.practice.tour.questionTextTitle",
        "Question content"
      ),
      body: t(
        "evaluation.practice.tour.questionText",
        "Real questions show context, the question itself, and any scoring rules in this area. Read carefully before answering."
      ),
    },
    {
      key: "verify",
      ref: targets.verify,
      title: t("evaluation.practice.tour.verifyTitle", "Verify your answer"),
      body: t(
        "evaluation.practice.tour.verify",
        "Click 'Verify my answer' to get a quick check on the practice question. During the real test, this gives you a completeness score to refine your answer."
      ),
    },
    {
      key: "microphone",
      ref: targets.microphone,
      title: t("evaluation.practice.tour.microphoneTitle", "Microphone"),
      body: t(
        "evaluation.practice.tour.microphone",
        "Use the microphone to dictate your answer instead of typing. Speak in short sentences and pause between each — the transcript appears as you talk."
      ),
    },
  ].filter((s) => s.ref?.current);

  const close = () => {
    sessionStorage.setItem(SESSION_KEY, "1");
    setStepIndex(-1);
  };

  if (stepIndex < 0 || stepIndex >= steps.length) return null;

  const step = steps[stepIndex];
  const isLast = stepIndex === steps.length - 1;

  return (
    <Portal>
      {/* Soft backdrop dimming the page (click to skip) */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        bottom={0}
        bg="blackAlpha.300"
        zIndex={1480}
        onClick={close}
      />
      <TourHighlight refTarget={step.ref} />
      <TourPopover refTarget={step.ref}>
        {({ placement }) => (
          <Box
            bg="white"
            borderWidth="1px"
            borderColor="purple.300"
            borderRadius="md"
            boxShadow="lg"
            p={4}
            position="relative"
          >
            <Arrow placement={placement} />
            <Text fontWeight="semibold" fontSize="md" mb={2} sx={{ marginBottom: "8px !important" }}>
              {step.title}
            </Text>
            <Text fontSize="sm" color="gray.700" mb={3}>
              {step.body}
            </Text>
            <Flex align="center" justify="space-between">
              <Text fontSize="xs" color="gray.500" sx={{ marginBottom: "0 !important" }}>
                {t("evaluation.practice.tour.progress", {
                  current: stepIndex + 1,
                  total: steps.length,
                  defaultValue: "{{current}} / {{total}}",
                })}
              </Text>
              <HStack spacing={2}>
                <Button size="sm" variant="ghost" onClick={close}>
                  {t("evaluation.practice.tour.skip", "Skip")}
                </Button>
                {isLast ? (
                  <Button size="sm" colorScheme="purple" onClick={close}>
                    {t("evaluation.practice.tour.done", "Got it")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    colorScheme="purple"
                    onClick={() => setStepIndex((i) => i + 1)}
                  >
                    {t("evaluation.practice.tour.next", "Next")}
                  </Button>
                )}
              </HStack>
            </Flex>
          </Box>
        )}
      </TourPopover>
    </Portal>
  );
};

/**
 * Highlight ring around the target. Tracks the target's bounding rect.
 */
const TourHighlight = ({ refTarget }) => {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const el = refTarget?.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    update();
    const id = setInterval(update, 100);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      clearInterval(id);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [refTarget]);

  if (!rect) return null;
  return (
    <Box
      position="fixed"
      top={`${rect.top - HIGHLIGHT_PADDING}px`}
      left={`${rect.left - HIGHLIGHT_PADDING}px`}
      width={`${rect.width + HIGHLIGHT_PADDING * 2}px`}
      height={`${rect.height + HIGHLIGHT_PADDING * 2}px`}
      borderWidth="2px"
      borderColor="purple.400"
      borderRadius="md"
      pointerEvents="none"
      zIndex={1490}
      boxShadow="0 0 0 9999px rgba(0,0,0,0.0)"
      transition="all 0.15s ease"
    />
  );
};

/**
 * Position the popover relative to the target. Tries below first, falls back
 * above if the popover wouldn't fit. Horizontally clamped to viewport.
 *
 * Renders the children via a render-prop so the popover content can adapt
 * (e.g. arrow direction) based on the chosen placement.
 */
const TourPopover = ({ refTarget, children }) => {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const el = refTarget?.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      // Choose vertical placement: prefer below, fall back above
      const spaceBelow = vh - r.bottom - VIEWPORT_MARGIN;
      const spaceAbove = r.top - VIEWPORT_MARGIN;
      let placement;
      let top;
      if (spaceBelow >= POPOVER_MAX_HEIGHT || spaceBelow >= spaceAbove) {
        placement = "below";
        top = r.bottom + ARROW_SIZE;
      } else {
        placement = "above";
        top = r.top - ARROW_SIZE - POPOVER_MAX_HEIGHT;
      }

      // Horizontal: center on target's centerline, clamp to viewport
      const targetCenterX = r.left + r.width / 2;
      let left = targetCenterX - POPOVER_WIDTH / 2;
      if (left < VIEWPORT_MARGIN) left = VIEWPORT_MARGIN;
      if (left + POPOVER_WIDTH > vw - VIEWPORT_MARGIN) {
        left = vw - VIEWPORT_MARGIN - POPOVER_WIDTH;
      }

      // Where the target's center sits relative to the popover (used for arrow x)
      const arrowLeft = Math.max(
        16,
        Math.min(POPOVER_WIDTH - 16, targetCenterX - left)
      );

      setPos({ top, left, placement, arrowLeft });
    };
    update();
    const id = setInterval(update, 100);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      clearInterval(id);
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [refTarget]);

  if (!pos) return null;
  return (
    <Box
      position="fixed"
      top={`${pos.top}px`}
      left={`${pos.left}px`}
      width={`${POPOVER_WIDTH}px`}
      maxHeight={`${POPOVER_MAX_HEIGHT}px`}
      zIndex={1500}
      overflow="visible"
    >
      <Box position="relative" style={{ "--tour-arrow-x": `${pos.arrowLeft}px` }}>
        {typeof children === "function"
          ? children({ placement: pos.placement })
          : children}
      </Box>
    </Box>
  );
};

/**
 * Triangle arrow on the popover, pointing back at the target.
 * Position anchored to a CSS variable set by the parent (`--tour-arrow-x`)
 * so it always points at the target's centerline even when the popover has
 * been clamped to the viewport edge.
 */
const Arrow = ({ placement }) => {
  const above = placement === "above";
  return (
    <Box
      position="absolute"
      left="var(--tour-arrow-x)"
      transform="translateX(-50%) rotate(45deg)"
      width={`${ARROW_SIZE * 2}px`}
      height={`${ARROW_SIZE * 2}px`}
      bg="white"
      borderWidth="1px"
      borderColor="purple.300"
      // Hide the two borders that face into the popover body
      sx={
        above
          ? { borderTopWidth: 0, borderLeftWidth: 0, bottom: `-${ARROW_SIZE}px` }
          : { borderBottomWidth: 0, borderRightWidth: 0, top: `-${ARROW_SIZE}px` }
      }
    />
  );
};

export default PracticeTour;
